<template>
 <section>
  <div class="driver-dashboard">
   <div class="driver-dashboard-container">
    <v-card class="filter-block">
     <Filters :placeHolder="'Search Driver Name/Number'" @get-filter="getFilters"></Filters>
    </v-card>

    <v-card class="list-block">
     <div class="list-header">
      <div class="count-block">
       <span class="count-chip count-chip--total">Total Drivers - <b>{{ pageCount }}</b></span>
       <span class="count-chip count-chip--active">Active - <b>{{ active }}</b></span>
       <span class="count-chip count-chip--inactive">Inactive - <b>{{ inactive }}</b></span>
      </div>
      <v-btn class="add-btn" color="#1f5c55" @click="openDialog({}, 'insert')">Add Driver <i class="fas fa-plus-circle"></i></v-btn>
     </div>

     <v-simple-table fixed-header height="calc(100vh - 345px)" class="driver-table">
      <thead>
       <tr>
        <th v-for="column in columns" :key="column.key" :class="{ sortable: column.sortable }" @click="column.sortable && toggleVersionSort()">
         <div class="th-content">
          <span>{{ column.label }}</span>
          <v-icon v-if="column.sortable" small class="sort-icon active">{{ sortDesc === -1 ? "mdi-chevron-down" : "mdi-chevron-up" }}</v-icon>
         </div>
        </th>
       </tr>
      </thead>
      <tbody>
       <tr v-if="!driverList.length">
        <td :colspan="columns.length" class="empty-row">No drivers found</td>
       </tr>
       <tr :class="{ deleted: driver.trashStatus === 1 }" v-for="(driver, index) in driverList" :key="driver.token || index">
        <td>
         <div class="driver-details">
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <span class="active-status" :class="{ inactive: driver.trash || driver.trashStatus }" v-on="on" v-bind="attrs"></span>
           </template>
           <span>{{ driver.trash ? "inactive" : "active" }}</span>
          </v-tooltip>

          <div class="avatar">
           <img v-if="driver.image" :src="driver.image" alt="" />
           <span v-else>{{ initials(driver.name) }}</span>
          </div>
          <div class="details">
           <span class="driver-name">{{ driver.name }}</span>
           <span class="driver-mobile">{{ driver.mobile }}</span>
          </div>
         </div>
        </td>

        <td>
         <button v-if="!driver.trash && driver.trashStatus === 0" type="button" class="soft-btn soft-btn--teal" @click="openDialog(driver, 'vehicleAdd')"><i class="fas fa-car"></i> View & Add Vehicle</button>
         <span v-else class="muted">---</span>
        </td>

        <td>
         <div class="doc-info" v-if="driver.trashStatus === 0 && licence(driver).no">
          <span class="doc-no">{{ licence(driver).no }}</span>
          <span v-if="licence(driver).expdate" class="doc-validity">
           Valid upto {{ getExpiry(licence(driver).expdate) }}
           <i class="fas fa-exclamation-triangle" v-if="checkExpiry(licence(driver).verified)" :class="{ 'expiry-soon': licence(driver).verified === 3 }"></i>
          </span>
         </div>
         <span v-else class="muted">---</span>
        </td>

        <td>
         <div class="doc-info" v-if="driver.trashStatus === 0 && (idProof(driver).no || policeClearance(driver).expdate)">
          <span v-if="idProof(driver).no" class="doc-no">{{ idProof(driver).no }}</span>
          <span v-if="policeClearance(driver).expdate" class="doc-validity">
           PoC valid upto {{ getExpiry(policeClearance(driver).expdate) }}
           <i class="fas fa-exclamation-triangle" v-if="checkExpiry(policeClearance(driver).verified)" :class="{ 'expiry-soon': policeClearance(driver).verified === 3 }"></i>
          </span>
         </div>
         <span v-else class="muted">---</span>
        </td>

        <td>
         <button v-if="driver.trashStatus === 0" type="button" class="soft-btn soft-btn--blue" @click="openDialog(driver, 'document')"><i class="far fa-file-alt"></i> Documents</button>
        </td>

        <td class="version-column">
         <span v-if="driver.trashStatus === 0 && driver.version" class="version-badge">{{ driver.version }}</span>
         <span v-else class="muted">---</span>
        </td>

        <td class="action-column">
         <div class="action-buttons" v-if="driver.trashStatus === 0">
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--edit" @click="openDialog(driver, 'edit')" v-on="on" v-bind="attrs"><i class="fas fa-pencil-alt"></i></button>
           </template>
           <span>Edit</span>
          </v-tooltip>
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--status" @click="openDialog(driver, 'statusChange')" v-on="on" v-bind="attrs">
             <i class="fas" :class="driver.trash ? 'fa-undo' : 'fa-ban'"></i>
            </button>
           </template>
           <span>{{ driver.trash ? "Activate" : "Deactivate" }}</span>
          </v-tooltip>
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--info" @click="openDialog(driver, 'remarks')" v-on="on" v-bind="attrs"><i class="fas fa-info-circle"></i></button>
           </template>
           <span>Remarks</span>
          </v-tooltip>
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--delete" @click="deleteDriver(driver)" v-on="on" v-bind="attrs"><i class="far fa-trash-alt"></i></button>
           </template>
           <span>Delete</span>
          </v-tooltip>
         </div>
         <div v-else class="deleted-row">
          <span>Driver Deleted</span>
          <v-tooltip bottom>
           <template v-slot:activator="{ on, attrs }">
            <button type="button" class="icon-btn icon-btn--restore" @click="restoreDriver(driver)" v-on="on" v-bind="attrs"><i class="fas fa-undo"></i></button>
           </template>
           <span>Restore</span>
          </v-tooltip>
         </div>
        </td>
       </tr>
      </tbody>
     </v-simple-table>

     <div class="list-footer">
      <span class="showing-text">Showing {{ showingFrom }} to {{ showingTo }} of {{ pageCount }} drivers</span>
      <v-pagination v-model="page" :length="totalPages" :total-visible="7" class="pagination"></v-pagination>
      <div class="per-page">
       <span>Show</span>
       <v-select v-model="perPage" :items="perPageOptions" dense outlined hide-details class="per-page-select" @change="changePerPage"></v-select>
       <span>per page</span>
      </div>
     </div>
    </v-card>
   </div>
   <v-dialog v-model="dialog" v-if="dialog" :width="dialogWidth" persistent>
    <v-card class="card-container">
     <DriverInsert v-if="dialogStatus === 'edit'" :token="driverDetails.token" :editStatus="true" @cancel-insert="cancelInsert" @driver-updated="updateDriver"></DriverInsert>

     <DriverInsert v-if="dialogStatus === 'insert'" :token="''" :editStatus="false" @cancel-insert="cancelInsert" @driver-updated="updateDriver"></DriverInsert>

     <DocumentInsert v-if="dialogStatus === 'document'" @recalling="test" :token="driverDetails.token" @cancel-insert="cancelInsert"></DocumentInsert>

     <DeactivationModal
      v-if="dialogStatus === 'statusChange'"
      :title="driverDetails.trash ? 'Activate' : 'Deactivate'"
      :subtitle="[driverDetails.name, driverDetails.mobile].filter(Boolean).join(' · ')"
      :token="driverDetails.token"
      @dialog-closed="cancelInsert"
      @change-status="updateDriver"
      :type="1"
     ></DeactivationModal>
     <VehicleAssign @cancel-insert="cancelInsert" v-if="dialogStatus === 'vehicleAdd'" :driverDetails="driverDetails" @update-driver="updateDriver"></VehicleAssign>

     <DriversRemarksList v-if="dialogStatus === 'remarks'" :token="driverDetails.token" :type="1" @edit-dialog="cancelInsert"></DriversRemarksList>
    </v-card>
   </v-dialog>

   <Confirm ref="confirm"></Confirm>
   <v-dialog v-model="deleteDialog" max-width="600px">
    <v-card>
     <v-card-title> Confirm Deletion </v-card-title>

     <v-card-text style="color: black; font-size: 16px; margin-top: 2px">
      Are you sure you want to delete the driver <strong style="margin-left: 2px">{{ driverdata.name }}</strong
      >?
     </v-card-text>
     <v-card-text>
      <v-textarea label="Remarks *" :rules="[(v) => !!v || 'Remarks are required']" v-model="deleteRemarks" outlined rows="3" placeholder="Enter remarks "></v-textarea>
     </v-card-text>
     <v-card-actions>
      <v-spacer></v-spacer>

      <v-btn outlined color="grey darken-1" text @click="deleteDialog = false"> Cancel </v-btn>

      <v-btn class="red darken-1" color="white" text @click="confirmDelete"> Delete </v-btn>
     </v-card-actions>
    </v-card>
   </v-dialog>

   <Loading v-if="loading"></Loading>
  </div>
 </section>
</template>

<script>
import Filters from "@/components/common/filter/Filters";
import axios from "@/axios/axios-global";
import moment from "moment";
import DriverInsert from "../driver-insert/DriverInsert.vue";
import DocumentInsert from "../document-insert/DocumentInsert.vue";
import DeactivationModal from "../../modal/DeactivationModal.vue";
import VehicleAssign from "../VehicleAssign.vue";
import Confirm from "../../common/Confirm.vue";
import Loading from "../../common/Loading.vue";
import DriversRemarksList from "../drivers-remarks-list/DriversRemarksList.vue";
export default {
 name: "DriverDashboardNew",
 components: { Confirm, Loading, Filters, DriverInsert, DocumentInsert, DeactivationModal, VehicleAssign, DriversRemarksList },
 data() {
  return {
   columns: [
    { key: "name", label: "Driver", sortable: false },
    { key: "vehicle", label: "Vehicles", sortable: false },
    { key: "licence", label: "Licence Details", sortable: false },
    { key: "idproof", label: "ID Proof & PoC Validity", sortable: false },
    { key: "documents", label: "Documents", sortable: false },
    // version is sorted by the server across all drivers
    { key: "version", label: "Version", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
   ],
   driverList: [],
   driverDetails: {},
   filters: { searchValue: "" },
   dialog: false,
   dialogStatus: "",
   dialogWidth: "1000",
   loading: false,
   page: 1,
   perPage: 10,
   perPageOptions: [10, 20, 50],
   pageCount: 0,
   sortDesc: 1,
   deleteDialog: false,
   deleteRemarks: "",
   driverdata: "",
   active: 0,
   inactive: 0,
   deleted: 0,
  };
 },
 computed: {
  totalPages() {
   return Math.max(1, Math.ceil(this.pageCount / this.perPage));
  },
  showingFrom() {
   return this.pageCount ? (this.page - 1) * this.perPage + 1 : 0;
  },
  showingTo() {
   return Math.min(this.page * this.perPage, this.pageCount);
  },
 },
 watch: {
  page() {
   this.getDriverList();
  },
 },
 mounted() {
  this.getDriverList();
 },
 methods: {
  test() {
   this.getDriverList();
  },
  async confirmDelete() {
   try {
    if (!this.deleteRemarks.trim()) {
     this.$toasted.show("Please Enter Remarks  for deletion", {
      position: "top-center",
      duration: 4000,
      type: "error",
     });
     return;
    }

    const payload = {
     remark: this.deleteRemarks,
     token: this.driverdata.token,
     trash: 1,
    };
    const response = await axios.post("driver/moveToTrash", payload);
    if (response.status === 200) {
     this.deleteDialog = false;
     this.deleteRemarks = "";
     this.getDriverList();
     this.$toasted.show("Driver Deleted Successfully", {
      position: "bottom-center",
      duration: 4000,
      type: "success",
     });
    } else {
     this.$toasted.show("An error occurred while deleting the driver", {
      position: "bottom-center",
      duration: 4000,
      type: "error",
     });
    }
   } catch (error) {
    console.error("Error deleting driver:", error);
    this.$toasted.show("An error occurred while deleting the driver", {
     position: "bottom-center",
     duration: 4000,
     type: "error",
    });
   }
  },

  deleteDriver(data) {
   this.deleteDialog = true;
   this.driverdata = data;
  },
  async restoreDriver(data) {
   const confirmTitle = "Confirm Restore";
   const confirmText = `Are you sure you want to restore the driver "${data.name}"?`;
   const confirmIcon = "fas fa-undo";
   const confirmed = await this.$refs.confirm.open(confirmTitle, confirmText, {
    color: "green",
    icon: confirmIcon,
   });
   if (confirmed) {
    try {
     const payload = {
      token: data.token,
      trash: 0,
     };
     const response = await axios.post("driver/restore", payload);
     this.$toasted.show(response.data, {
      position: "bottom-center",
      duration: 4000,
      type: response.status === 200 ? "success" : "error",
     });
    } catch (error) {
     this.$root.$emit("show_snackbar", { text: error, color: "#FF0000" });
     console.error(error);
    } finally {
     this.getDriverList();
    }
   }
  },
  toggleVersionSort() {
   this.sortDesc = this.sortDesc === 1 ? -1 : 1;
   this.reloadFromFirstPage();
  },

  getFilters(data) {
   this.filters = data || { searchValue: "" };
   this.reloadFromFirstPage();
  },
  changePerPage() {
   this.reloadFromFirstPage();
  },
  // page watcher reloads when the page changes; otherwise reload directly
  reloadFromFirstPage() {
   if (this.page === 1) this.getDriverList();
   else this.page = 1;
  },
  async getDriverList() {
   try {
    this.loading = true;
    const search = this.filters && this.filters.searchValue ? this.filters.searchValue.trim() : "";
    const response = await axios.post("driver/list", {
     search: search,
     page: this.page,
     limit: this.perPage,
     sort: this.sortDesc,
    });
    this.driverList = response.data.list || [];
    this.pageCount = response.data.total || 0;
    this.active = response.data.active;
    this.inactive = response.data.deactive;
    this.deleted = response.data.deleted;
   } catch (error) {
    console.error(error);
   } finally {
    this.loading = false;
   }
  },
  licence(driver) {
   return (driver.documents && driver.documents.licence) || {};
  },
  idProof(driver) {
   return (driver.documents && driver.documents.idproof) || {};
  },
  policeClearance(driver) {
   return (driver.documents && driver.documents.policeclearance) || {};
  },
  initials(name) {
   return (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  },
  getExpiry(date) {
   date = date ? moment(date, "YYYYMMDD").format("DD-MM-YYYY") : "";
   date = date && date != "Invalid date" ? date : "-";
   return date;
  },

  checkExpiry(data) {
   return [3, 4].includes(data) ? true : false;
  },

  openDialog(data, status) {
   this.dialog = true;
   this.driverDetails = data;
   this.dialogStatus = status;
   if (status === "document") this.dialogWidth = "1200";
   else if (status === "statusChange") this.dialogWidth = "580";
   else if (status === "vehicleAdd") this.dialogWidth = "640";
   else if (status === "restore") this.dialogWidth = "500";
   else this.dialogWidth = "1000";
  },
  updateDriver() {
   this.dialog = false;
   this.getDriverList();
  },
  cancelInsert() {
   this.dialog = false;
  },
 },
};
</script>

<style lang="scss" scoped>
$border: #e5e9f0;
$text: #1f2937;
$muted: #6b7280;
$header-bg: #f5f7fa;

.driver-dashboard {
 .driver-dashboard-container {
  padding: 10px;

  .filter-block {
   padding: 20px;
   background: #fff;
   border-radius: 8px;
   box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);

   // outlined search box with a search icon (Filters is shared, so styled here only)
   ::v-deep .filter-search-blk {
    .v-input {
     margin-top: 0;
     padding-top: 0;
    }
    .v-input__slot {
     min-height: 44px;
     padding: 0 12px 0 40px !important;
     border: 1px solid #cfd6e1;
     border-radius: 6px;
     background: #fff;
     position: relative;

     &::before,
     &::after {
      display: none;
     }
    }
    .v-input__slot .v-text-field__slot::before {
     content: "\f002";
     font-family: "Font Awesome 5 Free";
     font-weight: 900;
     position: absolute;
     left: -26px;
     top: 50%;
     transform: translateY(-50%);
     color: #4b5563;
     font-size: 15px;
    }
    input {
     font-size: 14px;
    }
   }
  }

  .list-block {
   margin-top: 20px;
   padding: 20px;
   border-radius: 8px;
   box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
  }

  .list-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 16px;

   .add-btn {
    color: #fff;
    font-weight: 700;
    letter-spacing: 0.04em;
    border-radius: 6px;

    .fas {
     margin-left: 8px;
    }
   }
  }

  .count-block {
   display: flex;
   align-items: center;
   gap: 12px;

   .count-chip {
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 14px;

    b {
     margin-left: 6px;
     color: $text;
    }
   }
   .count-chip--total {
    background: #e8f0fe;
    color: #374151;
   }
   .count-chip--active {
    background: #e6f4ea;
    color: #1e7b34;
   }
   .count-chip--inactive {
    background: #fde8e8;
    color: #c62828;
   }
  }

  .driver-table {
   border: 1px solid $border;
   border-radius: 8px;
   overflow: hidden;

   ::v-deep table {
    border-collapse: collapse;
   }

   th {
    background: $header-bg !important;
    color: $text !important;
    font-size: 13px !important;
    font-weight: 600;
    white-space: normal;
    border-right: 1px solid $border;
    border-bottom: 1px solid $border !important;
    padding: 10px 14px !important;

    &:last-child {
     border-right: none;
    }

    &.sortable {
     cursor: pointer;
     user-select: none;
    }

    .th-content {
     display: flex;
     align-items: center;
     justify-content: space-between;
     gap: 6px;
    }

    .sort-icon {
     color: #9ca3af;

     &.active {
      color: #1976d2;
     }
    }
   }

   td {
    font-size: 14px;
    color: $text;
    padding: 10px 14px !important;
    border-right: 1px solid $border;
    border-bottom: 1px solid $border !important;

    &:last-child {
     border-right: none;
    }
   }

   tbody tr:nth-child(even) {
    background: #fafbfc;
   }

   tbody tr:hover {
    background: #f3f6fb !important;
   }

   .empty-row {
    text-align: center;
    color: $muted;
    padding: 30px !important;
   }

   tr.deleted {
    background: #fff1f1 !important;
   }
  }

  .muted {
   color: #9ca3af;
  }

  .driver-details {
   display: flex;
   align-items: center;
   gap: 12px;

   ::v-deep .active-status {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    background: #1e9e3e;
    border-radius: 50%;
   }
   .inactive {
    background: #d32f2f;
   }

   .avatar {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e8f0fe;
    color: #1a5fd0;
    font-size: 14px;
    font-weight: 700;
    border: 1px solid #d6dae8;

    img {
     width: 100%;
     height: 100%;
     object-fit: cover;
    }
   }

   .details {
    display: grid;
    gap: 2px;

    .driver-name {
     font-weight: 700;
     font-size: 15px;
    }

    .driver-mobile {
     color: #4b5563;
     font-size: 13px;
    }
   }
  }

  .doc-info {
   display: grid;
   gap: 2px;

   .doc-no {
    font-weight: 600;
   }

   .doc-validity {
    font-size: 12px;
    color: $muted;
    white-space: nowrap;
   }

   .fas {
    color: rgb(218, 3, 3);
    font-size: 11px;
   }
   .expiry-soon {
    color: #ffbe01;
   }
  }

  .soft-btn {
   display: inline-flex;
   align-items: center;
   gap: 8px;
   padding: 8px 14px;
   font-size: 13px;
   font-weight: 700;
   border-radius: 6px;
   text-transform: uppercase;
   white-space: nowrap;
   transition: filter 0.15s;

   &:hover {
    filter: brightness(0.96);
   }

   .fas,
   .far {
    font-size: 14px;
   }
  }
  .soft-btn--blue {
   color: #1565a8;
   background: #e3f2fb;
  }
  .soft-btn--teal {
   color: #1f6f66;
   background: #e3f4f1;
  }

  .version-column {
   text-align: center;
  }

  .version-badge {
   display: inline-block;
   padding: 2px 10px;
   font-size: 13px;
   font-weight: 600;
   color: #4b5563;
   background: #eef0f3;
   border-radius: 6px;
  }

  .action-column {
   white-space: nowrap;

   .action-buttons,
   .deleted-row {
    display: flex;
    align-items: center;
    gap: 10px;
   }

   .deleted-row span {
    color: #c62828;
    font-weight: 600;
    font-size: 13px;
   }
  }

  .icon-btn {
   width: 40px;
   height: 40px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   border-radius: 8px;
   font-size: 15px;
   transition: filter 0.15s;

   &:hover {
    filter: brightness(0.95);
   }
  }
  .icon-btn--edit {
   color: #1976d2;
   background: #e8f0fe;
  }
  .icon-btn--status {
   color: #b45309;
   background: #fff4e0;
  }
  .icon-btn--info {
   color: #374151;
   background: #eef0f3;
  }
  .icon-btn--delete {
   color: #d32f2f;
   background: #fde8e8;
  }
  .icon-btn--restore {
   color: #1e7b34;
   background: #e6f4ea;
  }

  .list-footer {
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 16px;
   margin-top: 16px;

   .showing-text {
    color: #4b5563;
    font-size: 14px;
    white-space: nowrap;
   }

   .pagination {
    flex: 1;
   }

   ::v-deep .v-pagination__item,
   ::v-deep .v-pagination__navigation {
    box-shadow: none;
    border: 1px solid #d1d5db;
    border-radius: 6px;
   }

   ::v-deep .v-pagination__item--active {
    border-color: #1976d2;
   }

   .per-page {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #4b5563;
    font-size: 14px;
    white-space: nowrap;

    .per-page-select {
     width: 90px;
    }
   }
  }
 }
}
</style>
